'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  User,
} from 'firebase/auth'
import { createTenant, createSession } from '../../actions/auth'
import { app } from '../../lib/firebase'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

export default function SignUpForm() {
  const t = useTranslations('Signup')
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [orgNumber, setOrgNumber] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSignup = async (user: User) => {
    // Call Server Action to create Tenant in MongoDB
    const idToken = await user.getIdToken()
    const res = await createTenant(idToken, orgNumber, companyName)

    if (res.success) {
      // Success
      // Force refresh token to get new claims
      const newToken = await user.getIdToken(true)

      // Create Session
      await createSession(newToken)

      router.push('/dashboard')
    } else {
      setError(res.message || 'Tenant creation failed')
      // Ideally should revert Firebase user creation here
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const auth = getAuth(app)
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      )
      await handleSignup(userCredential.user)
    } catch (err: unknown) {
      console.error(err)
      setError(err instanceof Error ? err.message : 'Signup failed')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    if (!orgNumber || !companyName) {
      setError(t('googleRequiredFields'))
      return
    }

    setLoading(true)
    setError(null)
    const auth = getAuth(app)
    const provider = new GoogleAuthProvider()

    try {
      const result = await signInWithPopup(auth, provider)
      await handleSignup(result.user)
    } catch (err: unknown) {
      console.error('Google Sign In Error:', err)
      setError(err instanceof Error ? err.message : 'Google Sign In failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-2">
      <h1 className="text-3xl font-bold mb-6">{t('title')}</h1>
      <div className="w-full max-w-sm">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('companyName')}
            </label>
            <input
              type="text"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('orgNumber')}
            </label>
            <input
              type="text"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={orgNumber}
              onChange={(e) => setOrgNumber(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('email')}
            </label>
            <input
              type="email"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('password')}
            </label>
            <input
              type="password"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
          <div className="flex items-center justify-between mb-4">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 w-full"
              disabled={loading}
            >
              {loading ? t('creating') : t('submit')}
            </button>
          </div>
        </form>

        <div className="text-center mb-4 text-gray-500">{t('or')}</div>

        <button
          onClick={handleGoogleSignIn}
          type="button"
          disabled={loading}
          className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-center shadow-sm"
        >
          <Image
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            width={20}
            height={20}
            className="mr-3"
            alt="Google"
          />
          {t('googleSubmit')}
        </button>
      </div>
    </div>
  )
}
